import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { demeterArtificialIntelligenceApi, demeterNewsApi } from '../../../Apis/Apis';
import {
    DemeterArtificialIntelligenceModelModel,
    DemeterArtificialIntelligencePromptModel,
    DemeterArtificialIntelligencePromptType,
    DemeterNewsArticleStatus,
    ListDemeterArtificialIntelligenceModelsResponse,
    ListDemeterArtificialIntelligencePromptsResponse,
    ListDemeterNewsArticlesResponse,
    SortDirection,
    UpdateDemeterArtificialIntelligenceModelRequest,
} from '../../../Generated/Raven-Demeter';
import useApiWithoutAutoExecute from '../../Apis/Hooks/useApiWithoutAutoExecute';
import useMultipleApis from '../../Apis/Hooks/useMultipleApisHook';
import LinkButton, { LinkButtonType } from '../../Components/Form/Buttons/LinkButton';
import TextInput from '../../Components/Form/Inputs/TextInput';
import TableSkeleton from '../../Components/Layout/TableSkeleton';
import ActionsCellButton from '../../Components/Tables/ActionsCellButton/ActionsCellButton';
import Table from '../../Components/Tables/Table/Table';
import { TableHeader } from '../../Components/Tables/Table/TableDefinitions';
import useLanguage from '../../Services/Language/useLanguageHook';
import { STATUS_LABELS } from './articleActions';
import styles from './ArtificialIntelligence.module.scss';
import PromptEditor from './PromptEditor';

const ORDERED_STATUSES: DemeterNewsArticleStatus[] = [
    DemeterNewsArticleStatus.New,
    DemeterNewsArticleStatus.PreSelected,
    DemeterNewsArticleStatus.ToReview,
    DemeterNewsArticleStatus.ExpertReviewed,
    DemeterNewsArticleStatus.ArtifileIntelligentReviewed,
    DemeterNewsArticleStatus.Published,
    DemeterNewsArticleStatus.UnPublished,
    DemeterNewsArticleStatus.Abandon,
];

const PROMPT_TYPE_LABELS: Record<DemeterArtificialIntelligencePromptType, string> = {
    [DemeterArtificialIntelligencePromptType.PreFilter]: 'Pre-Filter',
    [DemeterArtificialIntelligencePromptType.FullRating]: 'Full Rating',
};

const formatCreatedTime = (createdTime?: string | null): string => {
    if (!createdTime) {
        return '-';
    }
    const date = new Date(createdTime);
    return Number.isNaN(date.getTime()) ? createdTime : date.toLocaleString();
};

const parseModelParameters = (parametersJson: string): { maxArticlesPerRun: string; secondsBetweenArticles: string; confidenceThreshold: string } => {
    try {
        const parsed = JSON.parse(parametersJson || '{}');
        return {
            maxArticlesPerRun: String(Number(parsed.MaxArticlesPerRun) || 3),
            secondsBetweenArticles: String(Number(parsed.SecondsBetweenArticles) || 2),
            confidenceThreshold: String(Number(parsed.ConfidenceThreshold) || 0.5),
        };
    } catch {
        return { maxArticlesPerRun: '3', secondsBetweenArticles: '2', confidenceThreshold: '0.5' };
    }
};

interface IModelEditValues {
    name: string;
    maxArticlesPerRun: string;
    secondsBetweenArticles: string;
    confidenceThreshold: string;
}

const makePromptContentCellRenderer =
    (onSelect: (guid: string) => void) =>
    (row: DemeterArtificialIntelligencePromptModel): JSX.Element | string => {
        const preview = (row.content ?? '').trim().substring(0, 100).concat('...');
        return (
            <div className={styles.link_cell}>
                <LinkButton
                    title={preview || row.demeterArtificialIntelligencePromptGuid}
                    type={LinkButtonType.White}
                    onClick={() => onSelect(row.demeterArtificialIntelligencePromptGuid)}
                    testId={`AdminPromptLink-${row.demeterArtificialIntelligencePromptGuid}`}
                    track={{ eventCategory: 'AiNewsRating', eventAction: 'PromptSelected' }}
                />
            </div>
        );
    };

const renderPromptType = (row: DemeterArtificialIntelligencePromptModel): string => {
    if (!row.promptType) {
        return '-';
    }
    return PROMPT_TYPE_LABELS[row.promptType] ?? row.promptType;
};

const renderPromptCreated = (row: DemeterArtificialIntelligencePromptModel): string => formatCreatedTime(row.createdTime);

const renderModelType = (row: DemeterArtificialIntelligenceModelModel): string => {
    if (!row.promptType) {
        return '-';
    }
    return PROMPT_TYPE_LABELS[row.promptType] ?? row.promptType;
};

const renderModelUpdated = (row: DemeterArtificialIntelligenceModelModel): string => formatCreatedTime(row.createdTime);

type ModelFieldChangeHandler = (guid: string, field: keyof IModelEditValues, value: string) => void;
type ModelSaveHandler = (model: DemeterArtificialIntelligenceModelModel) => void;

const makeModelNameCellRenderer =
    (modelEdits: Record<string, IModelEditValues>, onFieldChange: ModelFieldChangeHandler, saving: boolean) =>
    (row: DemeterArtificialIntelligenceModelModel): JSX.Element =>
        (
            <TextInput
                value={modelEdits[row.demeterArtificialIntelligenceModelGuid]?.name ?? ''}
                handleTextChange={(v) => onFieldChange(row.demeterArtificialIntelligenceModelGuid, 'name', v)}
                disabled={saving}
                testId={`ModelNameInput-${row.demeterArtificialIntelligenceModelGuid}`}
                track={{ eventCategory: 'AiNewsRating', eventAction: 'ModelNameChanged' }}
            />
        );

const makeModelMaxArticlesCellRenderer =
    (modelEdits: Record<string, IModelEditValues>, onFieldChange: ModelFieldChangeHandler, saving: boolean) =>
    (row: DemeterArtificialIntelligenceModelModel): JSX.Element =>
        (
            <TextInput
                value={modelEdits[row.demeterArtificialIntelligenceModelGuid]?.maxArticlesPerRun ?? ''}
                handleTextChange={(v) => onFieldChange(row.demeterArtificialIntelligenceModelGuid, 'maxArticlesPerRun', v)}
                disabled={saving}
                testId={`ModelMaxArticlesInput-${row.demeterArtificialIntelligenceModelGuid}`}
                track={{ eventCategory: 'AiNewsRating', eventAction: 'ModelMaxArticlesChanged' }}
            />
        );

const makeModelDelayCellRenderer =
    (modelEdits: Record<string, IModelEditValues>, onFieldChange: ModelFieldChangeHandler, saving: boolean) =>
    (row: DemeterArtificialIntelligenceModelModel): JSX.Element =>
        (
            <TextInput
                value={modelEdits[row.demeterArtificialIntelligenceModelGuid]?.secondsBetweenArticles ?? ''}
                handleTextChange={(v) => onFieldChange(row.demeterArtificialIntelligenceModelGuid, 'secondsBetweenArticles', v)}
                disabled={saving}
                testId={`ModelDelayInput-${row.demeterArtificialIntelligenceModelGuid}`}
                track={{ eventCategory: 'AiNewsRating', eventAction: 'ModelDelayChanged' }}
            />
        );

const makeModelThresholdCellRenderer =
    (modelEdits: Record<string, IModelEditValues>, onFieldChange: ModelFieldChangeHandler, saving: boolean) =>
    (row: DemeterArtificialIntelligenceModelModel): JSX.Element =>
        (
            <TextInput
                value={modelEdits[row.demeterArtificialIntelligenceModelGuid]?.confidenceThreshold ?? ''}
                handleTextChange={(v) => onFieldChange(row.demeterArtificialIntelligenceModelGuid, 'confidenceThreshold', v)}
                disabled={saving}
                testId={`ModelThresholdInput-${row.demeterArtificialIntelligenceModelGuid}`}
                track={{ eventCategory: 'AiNewsRating', eventAction: 'ModelThresholdChanged' }}
            />
        );

const makeModelSaveCellRenderer =
    (onSave: ModelSaveHandler, saving: boolean) =>
    (row: DemeterArtificialIntelligenceModelModel): JSX.Element =>
        (
            <ActionsCellButton
                text={saving ? 'Saving...' : 'Save'}
                handleClick={() => onSave(row)}
                disabled={saving}
                track={{ eventCategory: 'AiNewsRating', eventAction: 'ModelSaved' }}
            />
        );

const AdminTab: React.FC = () => {
    const [translations] = useLanguage();
    const [selectedPromptGuid, setSelectedPromptGuid] = useState<string | null>(null);
    const [modelEdits, setModelEdits] = useState<Record<string, IModelEditValues>>({});

    const [statsLoading, , statsResponses] = useMultipleApis<void, ListDemeterNewsArticlesResponse>(() =>
        ORDERED_STATUSES.map((status) =>
            demeterNewsApi.listDemeterNewsArticles(undefined, undefined, undefined, [status], undefined, SortDirection.Desc, 1, 0),
        ),
    );

    const statusCounts = useMemo(() => {
        const counts: Partial<Record<DemeterNewsArticleStatus, number>> = {};
        if (statsResponses) {
            statsResponses.forEach((response, index) => {
                counts[ORDERED_STATUSES[index]] = response?.totalCount ?? 0;
            });
        }
        return counts;
    }, [statsResponses]);

    const [promptsLoading, refreshPrompts, promptsResponse, promptsError] = useApiWithoutAutoExecute<void, ListDemeterArtificialIntelligencePromptsResponse>(
        () => demeterArtificialIntelligenceApi.listPrompts(),
        { showResponseErrorMessage: true, errorMessage: 'Failed to load prompts' },
    );

    const [modelsLoading, refreshModels, modelsResponse, modelsError] = useApiWithoutAutoExecute<void, ListDemeterArtificialIntelligenceModelsResponse>(
        () => demeterArtificialIntelligenceApi.listModels(),
        { showResponseErrorMessage: true, errorMessage: 'Failed to load models' },
    );

    const [modelSaving, saveModel] = useApiWithoutAutoExecute<UpdateDemeterArtificialIntelligenceModelRequest, object>(
        (request) => {
            if (!request) {
                return null;
            }
            return demeterArtificialIntelligenceApi.updateModel(request.demeterArtificialIntelligenceModelGuid, request);
        },
        { successMessage: 'Model updated', errorMessage: 'Failed to update model' },
    );

    useEffect(() => {
        refreshPrompts();
        refreshModels();
    }, []);

    const prompts = useMemo(() => {
        const rows = promptsResponse?.rows ?? [];
        return [...rows].sort((a, b) => {
            const dateA = a.createdTime ? new Date(a.createdTime).getTime() : 0;
            const dateB = b.createdTime ? new Date(b.createdTime).getTime() : 0;
            return dateA - dateB;
        });
    }, [promptsResponse]);

    const models = useMemo(() => {
        const rows = modelsResponse?.rows ?? [];
        return [...rows].sort((a, b) => {
            const dateA = a.createdTime ? new Date(a.createdTime).getTime() : 0;
            const dateB = b.createdTime ? new Date(b.createdTime).getTime() : 0;
            return dateA - dateB;
        });
    }, [modelsResponse]);

    useEffect(() => {
        if (models.length === 0) {
            return;
        }
        setModelEdits((prev) => {
            const next = { ...prev };
            models.forEach((m) => {
                if (!next[m.demeterArtificialIntelligenceModelGuid]) {
                    const params = parseModelParameters(m.parameters ?? '{}');
                    next[m.demeterArtificialIntelligenceModelGuid] = {
                        name: m.modelName ?? '',
                        maxArticlesPerRun: params.maxArticlesPerRun,
                        secondsBetweenArticles: params.secondsBetweenArticles,
                        confidenceThreshold: params.confidenceThreshold,
                    };
                }
            });
            return next;
        });
    }, [models]);

    const previousModelSavingReference = useRef(false);

    useEffect(() => {
        if (previousModelSavingReference.current && !modelSaving) {
            refreshModels();
        }
        previousModelSavingReference.current = modelSaving;
    });

    const handleModelFieldChange = useCallback((guid: string, field: keyof IModelEditValues, value: string) => {
        setModelEdits((prev) => ({ ...prev, [guid]: { ...prev[guid], [field]: value } }));
    }, []);

    const handleModelSave = useCallback(
        (model: DemeterArtificialIntelligenceModelModel) => {
            const edit = modelEdits[model.demeterArtificialIntelligenceModelGuid];
            if (!edit || !edit.name.trim()) {
                return;
            }
            const parameters = JSON.stringify({
                MaxArticlesPerRun: Number(edit.maxArticlesPerRun),
                SecondsBetweenArticles: Number(edit.secondsBetweenArticles),
                ConfidenceThreshold: Number(edit.confidenceThreshold),
            });
            saveModel({
                demeterArtificialIntelligenceModelGuid: model.demeterArtificialIntelligenceModelGuid,
                modelName: edit.name.trim(),
                parameters,
            });
        },
        [modelEdits, saveModel],
    );

    const promptColumns: TableHeader<DemeterArtificialIntelligencePromptModel>[] = useMemo(
        () => [
            {
                header: translations.words.content,
                minimumWidth: 300,
                cellRenderer: makePromptContentCellRenderer((guid) => setSelectedPromptGuid(guid)),
            },
            { header: translations.words.type, minimumWidth: 120, cellRenderer: renderPromptType },
            { header: translations.words.updated, minimumWidth: 160, cellRenderer: renderPromptCreated },
        ],
        [translations],
    );

    const modelColumns: TableHeader<DemeterArtificialIntelligenceModelModel>[] = useMemo(
        () => [
            {
                header: `${translations.words.model} ${translations.words.name}`,
                minimumWidth: 180,
                cellRenderer: makeModelNameCellRenderer(modelEdits, handleModelFieldChange, modelSaving),
            },
            {
                header: translations.words.maximum,
                minimumWidth: 100,
                cellRenderer: makeModelMaxArticlesCellRenderer(modelEdits, handleModelFieldChange, modelSaving),
            },
            { header: translations.words.delay, minimumWidth: 80, cellRenderer: makeModelDelayCellRenderer(modelEdits, handleModelFieldChange, modelSaving) },
            {
                header: translations.words.threshold,
                minimumWidth: 80,
                cellRenderer: makeModelThresholdCellRenderer(modelEdits, handleModelFieldChange, modelSaving),
            },
            { header: translations.words.type, minimumWidth: 100, cellRenderer: renderModelType },
            { header: translations.words.updated, minimumWidth: 140, cellRenderer: renderModelUpdated },
            { header: translations.words.actions, minimumWidth: 80, cellRenderer: makeModelSaveCellRenderer(handleModelSave, modelSaving) },
        ],
        [modelEdits, handleModelFieldChange, handleModelSave, modelSaving],
    );

    if (selectedPromptGuid) {
        return (
            <PromptEditor
                promptGuid={selectedPromptGuid}
                onBack={() => {
                    setSelectedPromptGuid(null);
                    refreshPrompts();
                }}
            />
        );
    }

    return (
        <div className={styles.page_container} data-testid="AiNewsRatingAdmin">
            <div className={styles.section_header}>
                <span className={styles.section_title}>{translations.words.content}</span>
            </div>

            {promptsLoading && <TableSkeleton numberOfColumns={3} numberOfRows={3} />}
            {promptsError && <div className={styles.error_message}>{promptsError}</div>}
            {!promptsLoading && !promptsError && <Table tableHeaders={promptColumns} tableData={prompts} tableStyle="alternatingRows" />}

            <hr className={styles.section_divider} />

            <div className={styles.section_header}>
                <span className={styles.section_title}>{translations.words.model}</span>
            </div>

            {modelsLoading && <TableSkeleton numberOfColumns={7} numberOfRows={3} />}
            {modelsError && <div className={styles.error_message}>{modelsError}</div>}
            {!modelsLoading && !modelsError && <Table tableHeaders={modelColumns} tableData={models} tableStyle="alternatingRows" />}

            <hr className={styles.section_divider} />

            <div className={styles.section_header}>
                <span className={styles.section_title}>{translations.words.summary}</span>
            </div>

            {statsLoading && <TableSkeleton numberOfColumns={8} numberOfRows={1} />}
            {!statsLoading && (
                <Table
                    tableHeaders={ORDERED_STATUSES.map((status) => ({
                        header: STATUS_LABELS[status],
                        minimumWidth: 100,
                        cellRenderer: (row: Partial<Record<DemeterNewsArticleStatus, number>>) => String(row[status] ?? '-'),
                    }))}
                    tableData={[statusCounts]}
                />
            )}
        </div>
    );
};

export default AdminTab;
